import { useRef, useState } from 'react';
import { App, Button, Tooltip, Upload } from 'antd';
import type { UploadProps } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AttachmentDto, AttachmentListDto } from '@pams/shared';
import { ATTACHMENT_MAX_BYTES, ATTACHMENT_MAX_PER_ARRANGEMENT } from '@pams/shared';
import { api } from '@/lib/api';
import type { ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { EmptyState } from '@/components/EmptyState';
import { READ_ONLY_TOOLTIP } from '@/lib/accessRole';
import { ATTACHMENT_COPY } from './copy';
import { PdfViewerModal, type PdfSource } from './PdfViewerModal';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C2b §3, widened C3a §3] THE FILES PINNED TO AN ORDER — AND THE ONES NOT PINNED TO ONE YET.
 *
 *  ⚠️ IT RENDERS NO `<Card>`. The card is the caller's, because the two callers frame it
 *  differently: the detail page gives it a titled card in the identity column, the builder gives
 *  it a bare section. One definition of what an attachment ROW is, two frames.
 *
 *  ─── ⚠️ [C3a §3] `arrangementId` IS OPTIONAL, AND THAT IS THE WHOLE OF THE PASS ───
 *
 *  §3 asks for a PDF to be attachable from Create page 1, where the draft DOES NOT EXIST YET —
 *  the builder creates it lazily, on the first armed autosave. So this takes `undefined`, the
 *  query is `enabled` on it, and a file chosen before there is an id is QUEUED by the caller.
 *
 *  ⚠️ THE FILE IS HELD; THE DRAFT IS NOT FORCED INTO EXISTENCE. The obvious alternative — the
 *  attach handler POSTing `/arrangements` itself — races the autosave and mints **two orders for
 *  one sitting**: `flush` sets `inFlight` and fires `save.mutate` while `savedId` is still
 *  `undefined`, and an independent POST reads the same `undefined` and fires a second. Both
 *  succeed, both burn a `reference` from the counter, and the operator's typing lands in one
 *  order while their PDF lands in the other, silently. It would also make the builder a SECOND
 *  writer of `/arrangements`, which is what the one-writer `flush` exists to prevent.
 *
 *  ⚠️ AND HOLDING IS ONLY SAFE BECAUSE THE GUARD KNOWS. `useUnsavedGuard`'s two arms both gain
 *  the queue, so leaving with a file still queued raises the prompt rather than dropping it.
 *
 *  ⚠️ IT IS ITS OWN QUERY KEY, A STRICT DESCENDANT of `detail(id)`. An upload invalidates only
 *  `attachments(id)` — never `arrangements.all`, which is a PREFIX of the record being edited and
 *  is the defect [P3b.3] cost a release. `saveKeyIsolation.test.ts`'s rule, on a second writer.
 *
 *  ⚠️ AND THE DOWNLOAD IS A BLOB, NOT AN `<a href>`. Every read on this API needs a bearer token
 *  and a plain anchor sends none — the browser would follow the link, get a 401 envelope and
 *  render JSON in a tab. The object URL is REVOKED immediately: an un-revoked one holds its bytes
 *  for the life of the document.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export type PendingAttachment = { key: string; file: File };

export const AttachmentList = ({
  arrangementId,
  canWrite,
  pending = [],
  onQueue,
  onDropPending,
  disabledReason,
}: {
  arrangementId: string | undefined;
  canWrite: boolean;
  /** Files chosen before the record existed. The caller owns them and drains them on save. */
  pending?: readonly PendingAttachment[];
  onQueue?: (file: File) => void;
  onDropPending?: (key: string) => void;
  /** Why the control is unavailable, when it is — shown instead of a silent refusal. */
  disabledReason?: string;
}): React.ReactElement => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [viewing, setViewing] = useState<PdfSource | null>(null);
  /* ⚠️ A REF, NOT STATE: it is read inside a callback and must not schedule a render. */
  const uploading = useRef(false);

  const attachments = useQuery({
    queryKey: queryKeys.arrangements.attachments(arrangementId ?? ''),
    queryFn: ({ signal }) => api.get<AttachmentListDto>(`/arrangements/${arrangementId ?? ''}/attachments`, { signal }),
    enabled: Boolean(arrangementId),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.attachments(arrangementId ?? '') });
  };

  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.post<AttachmentDto>(`/arrangements/${arrangementId ?? ''}/attachments`, form);
    },
    onSuccess: (added) => {
      invalidate();
      void message.success(ATTACHMENT_COPY.added(added.filename));
    },
    onError: (error: ApiError) => void message.error(error.message),
    onSettled: () => {
      uploading.current = false;
    },
  });

  const remove = useMutation({
    mutationFn: (attachmentId: string) =>
      api.delete(`/arrangements/${arrangementId ?? ''}/attachments/${attachmentId}`),
    onSuccess: invalidate,
    onError: (error: ApiError) => void message.error(error.message),
  });

  /*
   * ⚠️ [C3b §5] CLICKING AN ATTACHMENT **OPENS** IT. IT NO LONGER DOWNLOADS.
   *
   * This built an `<a download>` and clicked it, so the only way to read a PA attached to an
   * order was to save it and leave the app. `PdfViewerModal` is the one reader now, and the
   * download survives as a button INSIDE it — which is where it belongs, because a browser that
   * cannot render the PDF gives no error to detect and the escape hatch has to be visible.
   *
   * ⚠️ THE BLOB LIFECYCLE MOVED WITH IT. The old revoke-at-once was correct for an anchor click
   * and is wrong for an `<iframe>`, which reads the URL for as long as it is mounted; the modal
   * revokes on close instead. Keeping that reasoning in one file is the point of the move.
   */

  /*
   * ⚠️ `beforeUpload` RETURNS `false`, WHICH IS WHAT STOPS ANTD UPLOADING IT ITSELF.
   * AntD's `Upload` posts to its own `action` URL with its own XHR — no bearer token, no
   * base URL, no envelope handling. Returning `false` keeps the file picker and the drop zone
   * and hands us the `File`, so the request goes through the one client that refreshes tokens.
   */
  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    if (uploading.current) return false;
    if (file.size > ATTACHMENT_MAX_BYTES) {
      /* ⚠️ Stated here as well as on the server: the server's refusal costs a 5 MB round trip
         on a connection that may be slow, and the number comes from the same constant. */
      void message.error(ATTACHMENT_COPY.tooLarge);
      return false;
    }
    /*
     * ⚠️ [C3a §3] NO ID YET MEANS QUEUE, NEVER POST. The caller marks the form dirty and flushes,
     * so the ordinary one-writer save creates the record; this drains once `arrangementId` lands.
     */
    if (arrangementId === undefined) {
      onQueue?.(file);
      return false;
    }
    uploading.current = true;
    upload.mutate(file);
    return false;
  };

  const items = attachments.data?.items ?? [];
  const full = items.length + pending.length >= ATTACHMENT_MAX_PER_ARRANGEMENT;
  const blocked = disabledReason !== undefined;

  return (
    <div className="flex flex-col gap-2" data-testid="attachment-list">
      {canWrite ? (
        <Upload beforeUpload={beforeUpload} showUploadList={false} accept="application/pdf" disabled={full || blocked}>
          <Button
            size="small"
            loading={upload.isPending}
            disabled={full || blocked}
            data-testid="attach-file"
            title={disabledReason}
          >
            {ATTACHMENT_COPY.add}
          </Button>
        </Upload>
      ) : null}

      {items.length === 0 && pending.length === 0 ? (
        <EmptyState title={ATTACHMENT_COPY.emptyTitle} description={ATTACHMENT_COPY.emptyBody} />
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {items.map((attachment) => (
            <li key={attachment.id} className="flex flex-wrap items-center gap-2 " data-testid="attachment-row">
              {/*
                ⚠️ A BUTTON, NOT A LINK, AND THAT IS THE ONE PLACE `NameCell`'s ANCHOR RULE
                YIELDS. That rule exists so a destination can be ⌘-clicked and copied; a blob
                URL is neither — it is revoked a millisecond later and means nothing in another
                tab. A control that cannot be opened in a new tab must not look like one.
              */}
              <Tooltip title={attachment.filename}>
                <Button
                  type="link"
                  size="small"
                  className="max-w-full p-0"
                  onClick={() => {
                    setViewing({
                      path: `/arrangements/${arrangementId ?? ''}/attachments/${attachment.id}`,
                      filename: attachment.filename,
                    });
                  }}
                >
                  <span className="block truncate">{attachment.filename}</span>
                </Button>
              </Tooltip>
              <span className="text-muted-foreground text-xs">
                {ATTACHMENT_COPY.meta(attachment.sizeBytes, attachment.uploadedBy?.name ?? null)}
              </span>
              {canWrite && (
                <Button
                  type="text"
                  size="small"
                  danger
                  className="ml-auto"
                  loading={remove.isPending && remove.variables === attachment.id}
                  onClick={() => {
                    remove.mutate(attachment.id);
                  }}
                  aria-label={ATTACHMENT_COPY.removeLabel(attachment.filename)}
                  title={canWrite ? undefined : READ_ONLY_TOOLTIP}
                >
                  {ATTACHMENT_COPY.remove}
                </Button>
              )}
            </li>
          ))}

          {/*
            ⚠️ [C3a §3] A QUEUED FILE IS A REAL ROW, AND IT SAYS IT HAS NOT BEEN SAVED.
            An operator who chose a file and sees nothing assumes it did not take; one who sees
            it listed exactly like a stored file assumes it is safe. Neither is true yet.
          */}
          {pending.map((item) => (
            <li key={item.key} className="flex flex-wrap items-center gap-2" data-testid="attachment-pending">
              <span className="block max-w-full truncate text-sm">{item.file.name}</span>
              <span className="text-muted-foreground text-xs">{ATTACHMENT_COPY.pending}</span>
              {canWrite && onDropPending && (
                <Button
                  type="text"
                  size="small"
                  danger
                  className="ml-auto"
                  onClick={() => {
                    onDropPending(item.key);
                  }}
                  aria-label={ATTACHMENT_COPY.removeLabel(item.file.name)}
                >
                  {ATTACHMENT_COPY.remove}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/*
        ⚠️ [C3b §5] ONE MODAL FOR THE WHOLE LIST, OUTSIDE THE CONDITIONAL. Rendering it per row
        would mount one per attachment (ten of them) and `census.spec.ts` counts the JSX literal
        rather than the instances, but the wasted observers are real. It is `source`-gated, which
        is the same thing the three modals on the arrangements list are.
      */}
      <PdfViewerModal
        source={viewing}
        onClose={() => {
          setViewing(null);
        }}
      />
    </div>
  );
};
